import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconEmail, IconLink, IconList } from '../../../../shared/icon/icon'
import { useAlertRoutingAccess } from './access'
import { listTargets } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

const typeLabel = type => (type ? type.charAt(0).toUpperCase() + type.slice(1) : '')

// Where a target sends, in words. A webhook's URL is a write-only secret, so
// only whether it is set up shows, and the host (eTLD+1) the API sends when it
// has one.
export const destinationsOf = (t, label = typeLabel(t.type)) => {
  if (t.type === 'email') return (t.addresses || []).length ? plural(t.addresses.length, 'address', 'addresses') : 'No addresses yet'
  if (!Object.values(t.secrets || {}).some(s => s && s.set)) return `${label}: not set up`
  return t.host ? `${label}: ${t.host}` : `${label}: set up`
}

// Email targets are staff inboxes and get what they get today (spec D25), so
// the switch only means something for the other channels.
const personalText = t => (t.type === 'email' ? 'Included (email)' : t.allowPersonal ? 'Allowed' : 'No')

// The spec's type icon (§20.3), with the type's label beside it.
const TYPE_ICON = { email: IconEmail, webhook: IconLink }
const TypeCell = ({ type, label }) => {
  const Icon = TYPE_ICON[type]
  return <span className="ar-type" data-icon={type}>{Icon && <span aria-hidden="true"><Icon /></span>} {label}</span>
}

const ago = v => (v ? <span title={formatDateTime(v)}>{formatAgo(v)}</span> : '—')

export const TargetsTab = () => {
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const history = useHistory()
  const [rows, setRows] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  // The page stays mounted under a target drawer (same position in the
  // Switch), so the list reloads whenever the address changes: a create,
  // edit or delete shows as soon as the drawer closes (or moves to the new id).
  const { pathname } = useLocation()
  useEffect(() => {
    listTargets().then(v => { setError(null); setRows(v || []) }, err => setError(serverMessage(err, "Targets couldn't be loaded."), err))
  }, [pathname, setError])
  const labelOf = type => (catalog.actionTypes.find(a => a.type === type) || { label: typeLabel(type) }).label
  const getRows = useMemo(() => async (start, end) => (rows ? { data: rows.slice(start, end), count: rows.length } : { data: [], count: 0 }), [rows])
  return (
    <>
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
      {canEdit && <div className="ar-row mb-2">{catalog.actionTypes.map(a =>
        <Link key={a.type} className="btn sw-btn" to={`/admin/alert-routing/targets/new?type=${a.type}`}>New {a.label} target</Link>)}</div>}
      {!error && <FleetGrid title="targets" tableRef={tableRef}>
        <TableAgInfinite key={rows ? JSON.stringify(rows.map(r => [r.id, r.name, r.usedBy])) : 'x'} ref={tableRef} queryKey="" onGetRows={getRows}
          onRowClicked={r => history.push(`/admin/alert-routing/targets/${r.id}`)}
          emptyState={{ Icon: IconList, title: 'No targets', message: 'Add an email list or a webhook.' }}>
          <ColumnFormat field="name" header="Target" sortable={false} minWidth={180} flex={1.2}
            render={r => (r.data ? <>{r.value}{r.data.enabled === false && <> <Pill>Off</Pill></>}</> : '')} />
          <ColumnFormat field="type" header="Type" sortable={false} minWidth={120} render={r => (r.data ? <TypeCell type={r.value} label={labelOf(r.value)} /> : '')} />
          <ColumnFormat field="scopeType" header="Scope" sortable={false} minWidth={120}
            render={r => (r.data ? (r.value === 'platform' ? 'Platform' : r.data.organizationName || `Organization ${r.data.organizationID}`) : '')} />
          <ColumnFormat field="addresses" header="Destinations" sortable={false} minWidth={160} render={r => (r.data ? destinationsOf(r.data, labelOf(r.data.type)) : '')} />
          <ColumnFormat field="allowPersonal" header="Personal data" sortable={false} minWidth={160} render={r => (r.data ? personalText(r.data) : '')} />
          <ColumnFormat field="ratePerMinute" header="Per minute" sortable={false} minWidth={100} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="lastSuccessAt" header="Last success" sortable={false} minWidth={120} render={r => (r.data ? ago(r.value) : '')} />
          <ColumnFormat field="lastFailureAt" header="Last failure" sortable={false} minWidth={200} flex={1.2}
            render={r => (r.data ? (r.value ? <span className="sw-tone-bad">{formatAgo(r.value)}: {r.data.lastError || ''}</span> : '—') : '')} />
          <ColumnFormat field="usedBy" header="Used by" sortable={false} minWidth={90} render={r => (r.data ? plural(r.value || 0, 'route') : '')} />
        </TableAgInfinite>
      </FleetGrid>}
    </>
  )
}
